/**
 * Speech input — Web Speech API recognition
 */

class SpeechInput {
  constructor() {
    this.recognition = null
    this.isListening = false
    this.isSupported = false

    // Callbacks
    this.onStart = null
    this.onResult = null
    this.onInterim = null
    this.onEnd = null
    this.onError = null

    this._init()
  }

  _init() {
    // CL-152: guard for non-browser contexts (unit tests) and browsers with
    // no recognition support at all.
    const SpeechRecognition =
      typeof window !== 'undefined'
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : undefined

    if (!SpeechRecognition) {
      console.warn('[SpeechInput] Web Speech API not supported in this browser')
      this.isSupported = false
      return
    }

    this.isSupported = true
    this.recognition = new SpeechRecognition()
    this.recognition.continuous = false
    this.recognition.interimResults = true
    this.recognition.lang = 'en-US'
    this.recognition.maxAlternatives = 1

    this.recognition.onstart = () => {
      this.isListening = true
      this.onStart?.()
    }

    this.recognition.onresult = (event) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      if (interimTranscript) {
        this.onInterim?.(interimTranscript)
      }

      if (finalTranscript) {
        this.onResult?.(finalTranscript.trim())
      }
    }

    this.recognition.onend = () => {
      this.isListening = false
      this.onEnd?.()
    }

    this.recognition.onerror = (event) => {
      this.isListening = false
      let message = 'Speech recognition error'

      switch (event.error) {
        case 'no-speech':
          message = "I didn't hear anything. Try again."
          break
        case 'audio-capture':
          message = 'No microphone found. Check your device settings.'
          break
        case 'not-allowed':
          message = 'Microphone access denied. Please allow microphone access.'
          break
        case 'network':
          message = 'Network error. Check your connection.'
          break
        case 'aborted':
          // User stopped — not an error
          this.onEnd?.()
          return
        default:
          message = `Speech error: ${event.error}`
      }

      this.onError?.(message)
    }
  }

  /**
   * Start listening
   */
  start() {
    if (!this.isSupported) {
      this.onError?.(
        'Speech recognition is not supported in this browser. Use the text input instead.'
      )
      return
    }

    if (this.isListening) {
      this.stop()
      return
    }

    try {
      this.recognition.start()
    } catch (err) {
      // May already be running
      this.recognition.stop()
      setTimeout(() => {
        try {
          this.recognition.start()
        } catch (e) {
          /* Retry failed — Web Speech API may be in error state; user can try again */
        }
      }, 100)
    }
  }

  /**
   * Stop listening
   */
  stop() {
    if (this.recognition && this.isListening) {
      this.recognition.stop()
    }
  }

  /**
   * Toggle listening
   */
  toggle() {
    if (this.isListening) {
      this.stop()
    } else {
      this.start()
    }
  }

  /**
   * Clean up
   */
  destroy() {
    this.stop()
    if (this.recognition) {
      this.recognition.onstart = null
      this.recognition.onresult = null
      this.recognition.onend = null
      this.recognition.onerror = null
    }
  }
}

export { SpeechInput }
export default SpeechInput
